import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Save, FileText, Image as ImageIcon, Trash2, Plus, X, Lock } from "lucide-react";

const PROTECTED_KEYS = ["welcome"]; // mirrors backend

export default function Content() {
    const { user } = useAuth();
    const canEdit = user?.role === "chairman" || user?.role === "head";
    const [items, setItems] = useState([]);
    const [activeKey, setActiveKey] = useState(null);
    const [form, setForm] = useState({ title: "", body: "", image_url: "" });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(true);

    // Create-modal state
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState({ key: "", title: "", body: "", image_url: "", order: 50 });
    const [createError, setCreateError] = useState("");
    const [creating, setCreating] = useState(false);

    const load = async () => {
        const { data } = await api.get("/content");
        const list = data.items || [];
        setItems(list);
        if (list.length > 0 && !list.find((i) => i.key === activeKey)) {
            setActiveKey(list[0].key);
        }
        setLoading(false);
    };

    useEffect(() => {
        load();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const item = items.find((i) => i.key === activeKey);
        if (item) setForm({ title: item.title, body: item.body, image_url: item.image_url || "" });
    }, [activeKey, items]);

    const save = async () => {
        if (!activeKey) return;
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const { data } = await api.patch(`/content/${activeKey}`, form);
            setItems((prev) => prev.map((i) => (i.key === activeKey ? data : i)));
            setSuccess("Изменения сохранены");
            setTimeout(() => setSuccess(""), 2500);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (key, title) => {
        if (PROTECTED_KEYS.includes(key)) return;
        if (!window.confirm(`Удалить раздел «${title}»? Этот раздел исчезнет из меню бота.`)) return;
        try {
            await api.delete(`/content/${key}`);
            if (activeKey === key) setActiveKey(null);
            await load();
        } catch (err) {
            alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    const openCreate = () => {
        setCreateForm({ key: "", title: "", body: "", image_url: "", order: (items[items.length - 1]?.order ?? 0) + 1 });
        setCreateError("");
        setShowCreate(true);
    };

    const submitCreate = async (e) => {
        e.preventDefault();
        setCreating(true);
        setCreateError("");
        try {
            const payload = {
                ...createForm,
                key: createForm.key.toLowerCase().replace(/[^a-z0-9_]/g, "_"),
                order: parseInt(createForm.order, 10) || 0,
            };
            await api.post("/content", payload);
            setShowCreate(false);
            await load();
            setActiveKey(payload.key);
        } catch (err) {
            setCreateError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className="p-4 lg:p-10 h-[calc(100vh-3.5rem)] lg:h-screen flex flex-col" data-testid="content-page">
            <header className="mb-4 lg:mb-6 flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-1 lg:mb-2 hidden lg:block">Бот</div>
                    <h1 className="text-2xl lg:text-4xl font-heading font-medium tracking-tight text-slate-900">
                        Контент бота
                    </h1>
                    <p className="text-slate-500 mt-1 lg:mt-2 text-sm lg:text-base">
                        Тексты разделов меню, которые бот показывает абитуриентам
                    </p>
                </div>
                {canEdit && (
                    <button
                        onClick={openCreate}
                        data-testid="content-add-section"
                        className="inline-flex items-center gap-2 bg-brand text-white hover:bg-brand-hover transition-colors rounded-md px-4 py-2.5 font-medium text-sm"
                    >
                        <Plus className="h-4 w-4" /> Новый раздел
                    </button>
                )}
            </header>

            <div className="flex-1 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-3 lg:gap-5 min-h-0">
                <aside className="bg-white border border-slate-200 rounded-lg overflow-y-auto" data-testid="content-sections">
                    {loading ? (
                        <div className="p-4 text-sm text-slate-500">Загрузка...</div>
                    ) : items.length === 0 ? (
                        <div className="p-4 text-sm text-slate-500">Нет разделов</div>
                    ) : (
                        <ul>
                            {items.map((item) => {
                                const isActive = activeKey === item.key;
                                const isProtected = PROTECTED_KEYS.includes(item.key);
                                return (
                                    <li key={item.key} className="group relative">
                                        <button
                                            onClick={() => setActiveKey(item.key)}
                                            data-testid={`content-section-${item.key}`}
                                            className={`w-full text-left pl-4 pr-12 py-3 border-b border-slate-100 text-sm transition-colors flex items-center gap-3 ${
                                                isActive
                                                    ? "bg-slate-50 text-slate-900 font-medium"
                                                    : "text-slate-700 hover:bg-slate-50/60"
                                            }`}
                                        >
                                            <FileText className="h-4 w-4 text-slate-400 shrink-0" strokeWidth={1.5} />
                                            <span className="truncate flex-1">{item.title}</span>
                                            {isProtected && (
                                                <Lock className="h-3 w-3 text-slate-400 shrink-0" title="Защищён от удаления" />
                                            )}
                                        </button>
                                        {canEdit && !isProtected && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDelete(item.key, item.title);
                                                }}
                                                data-testid={`content-delete-${item.key}`}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100"
                                                title="Удалить раздел"
                                            >
                                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                                            </button>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </aside>

                <section className="bg-white border border-slate-200 rounded-lg overflow-hidden flex flex-col" data-testid="content-editor">
                    {!activeKey ? (
                        <div className="p-6 text-sm text-slate-500">Выберите раздел слева</div>
                    ) : (
                        <>
                            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                                <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">{activeKey}</div>
                                {canEdit && (
                                    <button
                                        onClick={save}
                                        disabled={saving}
                                        data-testid="content-save-button"
                                        className="inline-flex items-center gap-2 bg-brand text-white hover:bg-brand-hover disabled:opacity-60 transition-colors rounded-md px-4 py-2 font-medium text-sm"
                                    >
                                        <Save className="h-4 w-4" />
                                        {saving ? "Сохранение..." : "Сохранить"}
                                    </button>
                                )}
                            </div>

                            <div className="flex-1 overflow-y-auto p-6 space-y-5">
                                {error && (
                                    <div className="text-sm bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2" data-testid="content-error">
                                        {error}
                                    </div>
                                )}
                                {success && (
                                    <div className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-md px-3 py-2" data-testid="content-success">
                                        {success}
                                    </div>
                                )}

                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Заголовок</label>
                                    <input
                                        value={form.title}
                                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                                        disabled={!canEdit}
                                        data-testid="content-title-input"
                                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none disabled:bg-slate-50 disabled:text-slate-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Текст</label>
                                    <textarea
                                        value={form.body}
                                        onChange={(e) => setForm({ ...form, body: e.target.value })}
                                        rows={14}
                                        disabled={!canEdit}
                                        data-testid="content-body-input"
                                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none font-mono disabled:bg-slate-50 disabled:text-slate-500"
                                    />
                                    <p className="text-xs text-slate-400 mt-2">
                                        HTML-теги: &lt;b&gt; жирный &lt;/b&gt;, &lt;i&gt; курсив &lt;/i&gt;, &lt;a href="..."&gt;ссылка&lt;/a&gt;
                                    </p>
                                </div>

                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
                                        <ImageIcon className="h-3.5 w-3.5" /> URL изображения (необязательно)
                                    </label>
                                    <input
                                        value={form.image_url}
                                        onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                                        disabled={!canEdit}
                                        placeholder="https://..."
                                        data-testid="content-image-input"
                                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none disabled:bg-slate-50 disabled:text-slate-500"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </section>
            </div>

            {/* Create modal */}
            {showCreate && (
                <div
                    className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4"
                    onClick={() => setShowCreate(false)}
                >
                    <div
                        className="bg-white rounded-lg shadow-lg max-w-lg w-full"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="content-create-modal"
                    >
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                            <h2 className="font-heading font-medium text-slate-900">Новый раздел меню</h2>
                            <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-slate-600">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <form onSubmit={submitCreate} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                                    Ключ (латиница, например <code className="font-mono">scholarships</code>)
                                </label>
                                <input
                                    required
                                    value={createForm.key}
                                    onChange={(e) => setCreateForm({ ...createForm, key: e.target.value })}
                                    pattern="[a-zA-Z0-9_]+"
                                    minLength={2}
                                    maxLength={64}
                                    data-testid="content-create-key"
                                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
                                />
                                <p className="text-xs text-slate-400 mt-1.5">Только латиница, цифры и подчёркивания. Не меняется после создания.</p>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                                    Заголовок (видит абитуриент на кнопке)
                                </label>
                                <input
                                    required
                                    value={createForm.title}
                                    onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                                    placeholder="🎓 Стипендии"
                                    data-testid="content-create-title"
                                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Текст</label>
                                <textarea
                                    required
                                    value={createForm.body}
                                    onChange={(e) => setCreateForm({ ...createForm, body: e.target.value })}
                                    rows={6}
                                    data-testid="content-create-body"
                                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                                    Порядок (меньше — выше в меню)
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    value={createForm.order}
                                    onChange={(e) => setCreateForm({ ...createForm, order: e.target.value })}
                                    data-testid="content-create-order"
                                    className="w-32 border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
                                />
                            </div>

                            {createError && (
                                <div className="text-sm bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2" data-testid="content-create-error">
                                    {createError}
                                </div>
                            )}

                            <div className="flex justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowCreate(false)}
                                    className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-md"
                                >
                                    Отмена
                                </button>
                                <button
                                    type="submit"
                                    disabled={creating}
                                    data-testid="content-create-submit"
                                    className="px-4 py-2 text-sm font-medium bg-brand text-white hover:bg-brand-hover disabled:opacity-60 rounded-md transition-colors"
                                >
                                    {creating ? "Создание..." : "Создать раздел"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
